import { useTranslation } from 'react-i18next';
import { Languages, Check } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const LANGUAGES = [
  { code: 'en', labelKey: 'settings.language.english', flag: '🇺🇸' },
  { code: 'pt-BR', labelKey: 'settings.language.portuguese', flag: '🇧🇷' },
] as const;

export function LanguageSection() {
  const { t, i18n } = useTranslation();
  const current = i18n.language;

  const handleSelect = (code: string) => {
    if (code === current) return;
    i18n.changeLanguage(code);
    toast({
      title: t('settings.language.saved'),
      description: t('settings.language.savedDescription', {
        language: t(LANGUAGES.find(l => l.code === code)!.labelKey),
      }),
    });
  };

  return (
    <div className="rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[16px] p-5 shadow-[inset_0_1px_0_rgba(0,255,200,0.04)]">
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-6">
        <div className="p-1.5 rounded-md border border-[#00FFC8]/20 bg-[#00FFC8]/5">
          <Languages className="h-4 w-4 text-[#00FFC8]" />
        </div>
        <div>
          <h3 className="text-sm font-medium text-foreground">{t('settings.language.title')}</h3>
          <p className="text-[9px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
            {t('settings.language.subtitle')}
          </p>
        </div>
      </div>

      {/* Description */}
      <p className="text-[11px] text-[#A0A0A0] mb-4 leading-relaxed">
        {t('settings.language.description')}
      </p>

      {/* Language options */}
      <div className="space-y-1.5">
        {LANGUAGES.map(({ code, labelKey, flag }) => {
          const isSelected = current === code || (code === 'en' && current.startsWith('en'));
          return (
            <button
              key={code}
              onClick={() => handleSelect(code)}
              className={cn(
                'w-full flex items-center justify-between px-3 py-2.5 rounded-md border transition-all duration-200',
                isSelected
                  ? 'border-[rgba(0,255,200,0.3)] bg-[rgba(0,255,200,0.06)] text-[#00FFC8]'
                  : 'border-white/[0.04] bg-white/[0.01] text-foreground hover:bg-white/[0.03] hover:border-white/[0.08]'
              )}
            >
              <div className="flex items-center gap-2.5">
                <span className="text-base leading-none">{flag}</span>
                <span className="text-sm font-medium">{t(labelKey)}</span>
                <span className="text-[9px] font-mono tracking-widest uppercase text-white/30">{code}</span>
              </div>
              {isSelected && <Check className="h-3.5 w-3.5 text-[#00FFC8]" />}
            </button>
          );
        })}
      </div>

      {/* Auto-detect note */}
      <p className="mt-3 text-[9px] font-mono tracking-[0.1em] uppercase text-white/25">
        {t('settings.language.autoDetected')}
      </p>
    </div>
  );
}
